/**
 * /pages/my-designs — gallery page
 *
 * Reads saved designs from the localStorage library (window.AIDesignLibrary)
 * and renders a grid. Each tile has download buttons that re-render the design
 * at print quality (2K or 4K transparent PNG) via the Cloudflare Worker, then
 * downloads the file to the customer's device.
 *
 * Most of this script duplicates the print-render flow from ai-designer.js
 * because the two pages don't share an import system. If we ever extract a
 * shared helper module this is the obvious target.
 */
(function () {
  const root = document.querySelector(".my-designs");
  if (!root) return;

  const workerUrl = (root.dataset.workerUrl || "").replace(/\/$/, "");
  const turnstileSiteKey = root.dataset.turnstileSitekey || "";
  const printPrefix = root.dataset.printPrefix || "";
  const designerUrl = root.dataset.designerUrl || "/pages/design-with-ai";

  const gridEl = root.querySelector("[data-md-grid]");
  const emptyEl = root.querySelector("[data-md-empty]");
  const errorEl = root.querySelector("[data-md-error]");
  const TPL_TILE = root.querySelector('[data-md-template="tile"]');

  // ============= Turnstile =============
  let turnstileWidgetId = null;
  let pendingResolve = null;
  let pendingReject = null;

  function clearPending() { pendingResolve = null; pendingReject = null; }

  function renderTurnstileWhenReady() {
    if (!window.turnstile || !turnstileSiteKey) {
      setTimeout(renderTurnstileWhenReady, 250);
      return;
    }
    const widgetEl = root.querySelector(".cf-turnstile");
    if (!widgetEl || widgetEl.dataset.rendered === "1") return;
    turnstileWidgetId = window.turnstile.render(widgetEl, {
      sitekey: turnstileSiteKey,
      size: "invisible",
      callback: function (token) {
        if (pendingResolve) {
          const r = pendingResolve; clearPending(); r(token);
        }
      },
      "error-callback": function () {
        if (pendingReject) {
          const r = pendingReject; clearPending();
          r(new Error("Bot check failed — please refresh and try again."));
        }
      },
      "timeout-callback": function () {
        if (pendingReject) {
          const r = pendingReject; clearPending();
          r(new Error("Bot check timed out — please refresh and try again."));
        }
      },
    });
    widgetEl.dataset.rendered = "1";
  }
  renderTurnstileWhenReady();

  function executeTurnstile() {
    return new Promise(function (resolve, reject) {
      if (!window.turnstile || turnstileWidgetId === null) {
        return reject(new Error("Bot check not loaded yet — please wait a second."));
      }
      if (pendingReject) pendingReject(new Error("Replaced"));
      pendingResolve = resolve;
      pendingReject = reject;
      try { window.turnstile.reset(turnstileWidgetId); } catch (_) {}
      try { window.turnstile.execute(turnstileWidgetId); } catch (e) { clearPending(); return reject(e); }
      setTimeout(function () {
        if (pendingReject === reject) {
          clearPending();
          // Tear down the stale widget so the next call gets a fresh one.
          try {
            if (turnstileWidgetId !== null) window.turnstile.remove(turnstileWidgetId);
          } catch (_) {}
          turnstileWidgetId = null;
          const widgetEl = root.querySelector(".cf-turnstile");
          if (widgetEl) widgetEl.dataset.rendered = "";
          renderTurnstileWhenReady();
          reject(new Error("Bot check took too long — please click again."));
        }
      }, 60000);
    });
  }

  // ============= Network =============
  function postFormDataXHR(url, formData) {
    return new Promise(function (resolve, reject) {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.responseType = "json";
      xhr.timeout = 5 * 60 * 1000;
      xhr.onload = function () {
        const body = xhr.response;
        if (xhr.status >= 200 && xhr.status < 300) resolve(body);
        else {
          const msg = (body && body.error) || ("Server returned " + xhr.status);
          const err = new Error(msg); err.status = xhr.status; reject(err);
        }
      };
      xhr.onerror = function () { reject(new Error("Network error during upload")); };
      xhr.ontimeout = function () { reject(new Error("Upload timed out — try again")); };
      xhr.onabort = function () { reject(new Error("Upload was canceled")); };
      xhr.send(formData);
    });
  }

  // ============= UI helpers =============
  function showError(msg) {
    if (!msg) {
      errorEl.hidden = true;
      errorEl.textContent = "";
      return;
    }
    errorEl.hidden = false;
    errorEl.textContent = msg;
    setTimeout(function () { showError(""); }, 6000);
  }

  function safeFilename(prompt) {
    const slug = (prompt || "design").slice(0, 32).replace(/[^a-z0-9-_]+/gi, "-");
    return "ai-design-" + slug + ".png";
  }

  // ============= Render gallery =============
  function render() {
    if (!window.AIDesignLibrary) {
      showError("Design library couldn't load.");
      return;
    }
    const designs = window.AIDesignLibrary.list();
    gridEl.innerHTML = "";

    if (!designs.length) {
      emptyEl.hidden = false;
      gridEl.hidden = true;
      return;
    }
    emptyEl.hidden = true;
    gridEl.hidden = false;

    designs.forEach(function (design) {
      const tile = TPL_TILE.content.firstElementChild.cloneNode(true);

      const thumb = tile.querySelector("[data-md-thumb]");
      thumb.src = design.thumbnail || design.draftUrl;
      thumb.alt = design.prompt || "AI design";
      thumb.loading = "lazy";

      tile.querySelector("[data-md-prompt]").textContent =
        design.prompt || "(no prompt)";

      tile.querySelectorAll('[data-md-action="download"]').forEach(function (btn) {
        btn.addEventListener("click", function () {
          downloadPrintReady(design, btn);
        });
      });

      tile.querySelectorAll('[data-md-action="share"]').forEach(function (btn) {
        btn.addEventListener("click", function () {
          if (!window.AIDesignShare) return;
          btn.disabled = true;
          window.AIDesignShare.share({
            url: design.draftUrl,
            prompt: design.prompt,
          }).finally(function () {
            btn.disabled = false;
          });
        });
      });

      const deleteBtn = tile.querySelector('[data-md-action="delete"]');
      deleteBtn.addEventListener("click", function () {
        if (!confirm("Delete this design from your library?")) return;
        window.AIDesignLibrary.remove(design.id);
        render();
      });

      // "Submit to gallery / Get my free tee" button — appears alongside
      // existing tile actions. May return null if the promo's been claimed,
      // in which case we just don't render anything in this slot.
      if (window.AIDesignInspiration && window.AIDesignInspiration.attachSubmitButton) {
        const actionsEl = tile.querySelector(".my-designs__tile-actions");
        if (actionsEl) {
          const submitBtn = window.AIDesignInspiration.attachSubmitButton({
            workerUrl: workerUrl,
            url: design.draftUrl,
            prompt: design.prompt || "",
          });
          if (submitBtn) {
            // Match the gallery's existing button styling so it doesn't look bolted-on.
            submitBtn.classList.add("my-designs__btn", "my-designs__btn--ghost");
            actionsEl.appendChild(submitBtn);
          }
        }
      }

      gridEl.appendChild(tile);
    });
  }

  // ============= Print-ready download =============
  /**
   * Download the saved draft URL directly — XHR-based to bypass Shopify's
   * pixel-script fetch wrappers.
   */
  async function downloadPrintReady(design, btn) {
    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = "Downloading…";

    try {
      if (!window.AIDesignFetch) {
        throw new Error("Library not loaded yet — refresh and try again.");
      }
      const blob = await window.AIDesignFetch.fetchBlob(design.draftUrl);
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = safeFilename(design.prompt);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(objectUrl); }, 30000);

      window.AIDesignLibrary.touch(design.id);

      btn.textContent = "Downloaded ✓";
      setTimeout(function () {
        btn.textContent = originalLabel;
        btn.disabled = false;
      }, 2500);
    } catch (err) {
      console.error(err);
      showError(err.message || "Couldn't download.");
      btn.textContent = originalLabel;
      btn.disabled = false;
    }
  }

  // ============= Cloud sync banner =============
  /**
   * Render a small banner above the grid showing cloud sync state:
   *   - "Signed in as <email>" with a sign-out option, OR
   *   - "Lost your designs? Get a link" recovery prompt
   * Recovery opens a tiny inline prompt — no full modal needed here.
   */
  function injectCloudStyles() {
    if (document.getElementById("md-cloud-styles")) return;
    const s = document.createElement("style");
    s.id = "md-cloud-styles";
    s.textContent =
      ".md-cloud{display:flex;align-items:center;gap:12px;margin:0 0 16px;padding:12px 14px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:10px;font:13px/1.4 inherit;color:#1a1a1a}" +
      ".md-cloud--signed{background:#f0fdf4;border-color:#bbf7d0}" +
      ".md-cloud__icon{flex:0 0 auto;width:28px;height:28px;border-radius:50%;background:#e2e8f0;display:flex;align-items:center;justify-content:center;font-size:14px}" +
      ".md-cloud--signed .md-cloud__icon{background:#22c55e;color:#fff}" +
      ".md-cloud__text{flex:1 1 auto;min-width:0}" +
      ".md-cloud__text strong{color:#1a1a1a;font-weight:700}" +
      ".md-cloud__action{flex:0 0 auto;background:transparent;border:0;color:#ff6b00;font-weight:700;font-size:13px;cursor:pointer;padding:6px 10px;border-radius:6px}" +
      ".md-cloud__action:hover{background:rgba(255,107,0,0.08);text-decoration:none}" +
      ".md-cloud__form{display:flex;gap:8px;flex:1 1 auto;flex-wrap:wrap;align-items:center}" +
      ".md-cloud__input{flex:1 1 180px;min-width:0;padding:8px 12px;border:1px solid #d4d4d4;border-radius:8px;font:14px inherit;color:#1a1a1a;background:#fff}" +
      ".md-cloud__input:focus{outline:0;border-color:#ff6b00;box-shadow:0 0 0 3px rgba(255,107,0,0.15)}" +
      ".md-cloud__submit{padding:8px 14px;border:0;border-radius:8px;background:#1a1a1a;color:#fff;font:700 13px inherit;cursor:pointer}" +
      ".md-cloud__submit:disabled{opacity:.6;cursor:not-allowed}" +
      ".md-cloud__cancel{background:transparent;border:0;color:#5a5a5a;font:13px inherit;cursor:pointer;padding:8px}";
    document.head.appendChild(s);
  }

  function renderCloudBanner() {
    if (!window.AIDesignLibrary || !window.AIDesignLibrary.cloud) return;
    const cloud = window.AIDesignLibrary.cloud;
    let bar = root.querySelector("[data-md-cloud]");
    // Only render a banner when the customer is actually signed in. The
    // "lost your designs / send me a link" recovery banner was removed
    // until a Klaviyo flow is built to actually deliver the link — without
    // a flow, the recovery feature can't be honoured, so we hide it.
    if (!cloud.isSignedIn()) {
      if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
      return;
    }
    injectCloudStyles();
    if (!bar) {
      bar = document.createElement("div");
      bar.setAttribute("data-md-cloud", "");
      bar.className = "md-cloud";
      const insertBefore = gridEl || emptyEl || root.firstElementChild;
      if (insertBefore && insertBefore.parentNode) {
        insertBefore.parentNode.insertBefore(bar, insertBefore);
      } else {
        root.insertBefore(bar, root.firstChild);
      }
    }
    bar.classList.add("md-cloud--signed");
    bar.innerHTML =
      '<div class="md-cloud__icon" aria-hidden="true">✓</div>' +
      '<div class="md-cloud__text">Synced to <strong>' + escapeHtml(cloud.getEmail() || "your email") + '</strong> — your designs are saved across all your devices.</div>' +
      '<button type="button" class="md-cloud__action" data-md-signout>Sign out</button>';
    bar.querySelector("[data-md-signout]").addEventListener("click", function () {
      if (!confirm("Sign out of cloud sync? Your local designs stay on this device.")) return;
      cloud.signOut();
      renderCloudBanner();
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c];
    });
  }

  // Initial render — wait briefly for the library script to load.
  function start() {
    if (!window.AIDesignLibrary) {
      setTimeout(start, 100);
      return;
    }
    // Register the worker URL with the cloud module so it can sync.
    if (window.AIDesignLibrary.cloud && workerUrl) {
      window.AIDesignLibrary.cloud.setWorkerUrl(workerUrl);
    }
    render();
    renderCloudBanner();
    // If there's a token (either from previous session or magic-link arrival),
    // pull cloud library and re-render to include cross-device designs.
    if (window.AIDesignLibrary.cloud && window.AIDesignLibrary.cloud.getToken()) {
      window.AIDesignLibrary.cloud.pull().then(function (res) {
        if (res && res.merged > 0) render();
        renderCloudBanner();
      }).catch(function (e) {
        console.warn("Cloud pull failed", e);
        renderCloudBanner();
      });
    }
  }
  start();
})();